import { Injectable, Injector, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AlertController, ModalController } from '@ionic/angular';
import { GoogleAnalyticsService } from '@timechef/core/analytics';
import {
  ApiOrdersService,
  ApiRestaurantsService,
  ApiSurveysService,
  AppError,
  CheckCartRequest,
  CreateOrderRequest,
  DishDto,
  GetRestaurantsResult,
  GetSurveyResult,
  NotificationParameterFlags,
  RestaurantAnimationDto,
  RestaurantServiceValues,
  RestaurantSousTypeValues,
  SurveyReplyDto,
  TypeNotification,
  TypologyDto,
} from '@timechef/core/api';
import { AuthRepository } from '@timechef/core/auth';
import { I18nService } from '@timechef/core/i18n/i18n.service';
import { InAppReviewService } from '@timechef/core/services/in-app-review.service';
import { InteractionService } from '@timechef/core/services/interaction.service';
import { PushNotificationService } from '@timechef/core/services/push-notification.service';
import { avg, ensureDate, formatWithLang } from '@timechef/core/utils';
import { compareArray } from '@timechef/core/utils/array-utils';
import { CouldBeCached, IN_CACHE, IN_CACHE_SOURCE } from '@timechef/core/utils/elf-utils';
import { isNullOrUndefined } from '@timechef/core/utils/js-utils';
import { AccountRepository } from '@timechef/features/account/state';
import { AntiGaspiAlertSource } from '@timechef/features/account/state/account.models';
import { AnimationsRepository } from '@timechef/features/animations/state';
import { NotificationsRepository, NotificationsService } from '@timechef/features/notifications/state';
import { endOfDay, isAfter, isSameDay } from 'date-fns';
import { Subscription, catchError, firstValueFrom, forkJoin, map, mergeMap, of, switchMap, tap, timer } from 'rxjs';
import { AllergensModalComponent } from '../shared/components/allergens-modal/allergens-modal.component';
import { CarbonscoreModalComponent } from '../shared/components/carbonscore-modal/carbonscore-modal.component';
import { GreenscoreModalComponent } from '../shared/components/greenscore-modal/greenscore-modal.component';
import { NutriscoreModalComponent } from '../shared/components/nutriscore-modal/nutriscore-modal.component';
import { RestaurantDescriptionComponent } from '../shared/components/restaurant-description/restaurant-description.component';
import { SelectDishInformationComponent } from '../shared/components/select-dish-information/select-dish-information.component';
import { ServiceSelectionModalComponent } from '../shared/components/service-selection-modal/service-selection-modal.component';
import { TypologiesModalComponent } from '../shared/components/typologies-modal/typologies-modal.component';
import { CartModalComponent } from '../shared/containers/cart-modal/cart-modal.component';
import { DishDetailsComponent, OPEN_CART_MODAL_ROLE } from '../shared/containers/dish-details/dish-details.component';
import { FormulaModalComponent } from '../shared/containers/formula-modal/formula-modal.component';
import { MenuSelectionModalComponent } from '../shared/containers/menu-selection-modal/menu-selection-modal.component';
import { RestaurantFluiditeComponent } from '../shared/containers/restaurant-fluidite/restaurant-fluidite.component';
import { SurveyCampaignModalComponent } from '../shared/containers/survey-campaign-modal/survey-campaign-modal.component';
import { map_GetFluiditeResult_FluiditeDetails, map_RestaurantDto_Restaurant, map_getMenu_MenuDto } from './restaurant.utils';
import { CartFormula, CartItem, DishInformationEnum, Menu, Restaurant, RestaurantServiceData, SelectableFormula, SelectableMenu } from './restaurants.models';
import { RestaurantsRepository, RestaurantsStatusKeys, buildFluiditeDsKey, buildSurveyDsKey } from './restaurants.repository';

const EVERY_10_SEC = 10000;
const EVERY_MINUTE = 60000;
const SURVEY_NOTE_ASK_REVIEW = 4;

@Injectable({
  providedIn: 'root',
})
export class RestaurantsService {
  authRepo: AuthRepository;
  animationsRepo: AnimationsRepository;

  // Todo DDJ : Create a master service to avoid service inter-dependency (not urgent)
  notificationsService = inject(NotificationsService);
  notificationRepo = inject(NotificationsRepository);
  pushNotifService = inject(PushNotificationService);

  constructor(
    private alert: AlertController,
    private accountRepo: AccountRepository,
    private i18n: I18nService,
    private inAppReview: InAppReviewService,
    injector: Injector,
    private interaction: InteractionService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private modalCtrl: ModalController,
    private ordersApi: ApiOrdersService,
    private restaurantsApi: ApiRestaurantsService,
    private restaurantsRepo: RestaurantsRepository,
    private surveysApi: ApiSurveysService,
  ) {
    this.authRepo = injector.get(AuthRepository);
    this.animationsRepo = injector.get(AnimationsRepository);
  }

  private isOpenSub?: Subscription;
  private fluiditeSub?: Subscription;

  startMonitoringRestaurants() {
    this.stopMonitoringRestaurants();

    this.isOpenSub = timer(0, EVERY_10_SEC) // every 10 sec we check is Open
      .pipe(
        tap(() => {
          this.restaurantsRepo.getRestaurants().forEach((r) => {
            this.restaurantsRepo.setIsOpen(r.id);
          });
        }),
      )
      .subscribe();

    this.fluiditeSub = timer(0, EVERY_MINUTE) // every minute we check fluidite
      .pipe(
        map(() => {
          return this.restaurantsRepo.getRestaurants().filter((r) => {
            return r.hasFluidite && r.isOpen;
          });
        }),
        switchMap((restaurants) => {
          return forkJoin(
            restaurants.map((r) =>
              this.getCurrentFluidite(r.id).pipe(
                catchError(() => {
                  return of();
                }),
              ),
            ),
          );
        }),
      )
      .subscribe();
  }

  stopMonitoringRestaurants() {
    if (this.isOpenSub) {
      this.isOpenSub.unsubscribe();
      this.isOpenSub = undefined;
    }

    if (this.fluiditeSub) {
      this.fluiditeSub.unsubscribe();
      this.fluiditeSub = undefined;
    }
  }

  /** Set active restaurant and return if the restaurant is valid or not */
  setActiveRestaurantId(id: number) {
    if (!this.restaurantsRepo.getRestaurantById(id)) {
      // if restaurant is not here (either empty restaurants or old list), we fetch new restaurants
      return this.getRestaurants().pipe(
        map((_result) => {
          const isRestaurantInNewList = !!this.restaurantsRepo.getRestaurantById(id);
          if (!isRestaurantInNewList) {
            return false;
          }

          this.restaurantsRepo.setActiveRestaurantId(id);
          return true;
        }),
      );
    }

    this.restaurantsRepo.setActiveRestaurantId(id);

    return of(true);
  }

  getRestaurants() {
    // todo : offline handling ?
    return this.restaurantsApi.getRestaurants({ SiteName: this.authRepo.getCurrentSiteName() }).pipe(
      this.restaurantsRepo.restaurantsDS.trackRequestStatus(),
      this.restaurantsRepo.restaurantsDS.skipWhileCached(IN_CACHE_SOURCE),
      tap((apiData: CouldBeCached<GetRestaurantsResult>) => {
        if (apiData === IN_CACHE) {
          return;
        }

        this.restaurantsRepo.setRestaurants(apiData.restaurants.map(map_RestaurantDto_Restaurant));
        this.animationsRepo.addAnimationsFromRestaurants(
          apiData.restaurants.map((r) => r.animation).filter((a) => !isNullOrUndefined(a)) as RestaurantAnimationDto[],
        );
      }),
    );
  }

  getRestaurantSurvey(restaurantId: number, surveyId: number, surveyCampaignId: number) {
    return this.surveysApi.getBySurveyid({ SurveyId: surveyId, SurveyCampaignId: surveyCampaignId }).pipe(
      this.restaurantsRepo.restaurantSurveyCampaignDetailDS.trackRequestStatus({ key: buildSurveyDsKey(restaurantId) }),
      this.restaurantsRepo.restaurantSurveyCampaignDetailDS.skipWhileCached({ ...IN_CACHE_SOURCE, key: buildSurveyDsKey(restaurantId) }),
      tap((apiData: CouldBeCached<GetSurveyResult>) => {
        if (apiData === IN_CACHE) {
          return;
        }
        this.restaurantsRepo.setRestaurantSurveyCampaignDetails(restaurantId, apiData);
      }),
    );
  }

  getSurveyChat(responseId: number) {
    return this.surveysApi.getChatsByResponseid({ ResponseId: responseId }).pipe(
      map((x) => ({
        ...x,
        conviveCommentDate: ensureDate(x.conviveCommentDate),
        managerCommentDate: x.managerCommentDate ? ensureDate(x.managerCommentDate) : undefined,
      })),
      this.restaurantsRepo.trackRequestStatus(RestaurantsStatusKeys.RestaurantSurveyResponse),
      tap(() => this.restaurantsRepo.setGetSurveyChatSuccess()),
    );
  }

  getCurrentFluidite(restaurantId: number) {
    return this.restaurantsApi.getByRestaurantidCurrentfluidite({ RestaurantId: restaurantId }).pipe(
      tap((data) => {
        this.restaurantsRepo.setRestaurantCurrentFluidite(restaurantId, data.current);
      }),
    );
  }

  getRestaurantMenu(restaurantId: number, params: { initMenuFilter: boolean }) {
    return this.restaurantsApi.getByRestaurantidMenus({ RestaurantId: restaurantId }).pipe(
      this.restaurantsRepo.restaurantDetailDS.trackRequestStatus({ key: restaurantId }),
      tap((data) => {
        const restaurant = this.restaurantsRepo.getRestaurantById(restaurantId)!;
        this.restaurantsRepo.setRestaurantMenu(
          restaurantId,
          data.menus.map((m) => map_getMenu_MenuDto(m, restaurant)),
        );
        if (params.initMenuFilter && data.menus.length > 0) {
          const dates = data.menus.map((x) => ensureDate(x.date));
          const closureDates = data.menus.filter((x) => x.isClosed).map((x) => ensureDate(x.date));
          this.restaurantsRepo.initMenuFilters(dates, closureDates);
          this.restaurantsRepo.setMenuFilterCurrentDate(dates[0]);
        }
      }),
    );
  }

  getOrdersSummary(restaurantId: number) {
    const { menus } = this.restaurantsRepo.getActiveRestaurant()!;

    if (menus.length == 0) return of();

    const startDate = menus[0].date;
    const endDate = menus.length > 1 ? menus[menus.length - 1].date : endOfDay(ensureDate(startDate)).toISOString();

    return this.ordersApi.getOrderssummary({ RestaurantId: restaurantId, StartDate: startDate, EndDate: endDate }).pipe(
      this.restaurantsRepo.trackRequestStatus(RestaurantsStatusKeys.OrdersSummary),
      tap((data) => {
        this.restaurantsRepo.setRestaurantOrders(data.orders);
        this.restaurantsRepo.refreshRestaurantOrderSettings(restaurantId, data.orderSettings);
      }),
    );
  }

  getFluiditeRates(restaurantId: number) {
    return this.restaurantsApi.getByRestaurantidFluiditerates({ RestaurantId: restaurantId }).pipe(
      this.restaurantsRepo.restaurantFluiditeDetailsDS.trackRequestStatus({ key: buildFluiditeDsKey(restaurantId) }),
      map((data) => map_GetFluiditeResult_FluiditeDetails(data)),
      tap((data) => {
        this.restaurantsRepo.setRestaurantFluiditeDetails(restaurantId, data);
      }),
    );
  }

  getRestaurantExterne(restaurantId: number, options?: { table?: string }) {
    this.restaurantsRepo.setActiveRestaurantId(restaurantId);
    return this.restaurantsApi.getByRestaurantidHomeRedirection({ RestaurantId: restaurantId, Table: options?.table }).pipe(
      this.restaurantsRepo.trackRequestStatus(RestaurantsStatusKeys.RestaurantExterne),
      tap((data) => {
        this.restaurantsRepo.setRestaurantExterne(data);
      }),
    );
  }

  replyToSurveyCampaign(restaurant: Restaurant, replies: SurveyReplyDto[], comments: string) {
    return this.surveysApi
      .postReply({
        body: {
          comments,
          replies,
          restaurantId: restaurant.id,
          surveyCampaignId: restaurant.surveyCampaign!.campaignId,
        },
      })
      .pipe(
        this.restaurantsRepo.trackRequestStatus(RestaurantsStatusKeys.ReplyToRestaurantSurvey),
        tap(() => {
          this.restaurantsRepo.replyToSurvey(restaurant.id);
        }),
        mergeMap(async () => {
          // rename keys
          const alert = await this.alert.create({
            header: this.i18n.translate('RESTAURANT_SERVICE.CONFIRM_ALERT.HEADER'),
            message: this.i18n.translate(`RESTAURANT_SERVICE.CONFIRM_ALERT.${replies[0].note}`),
            buttons: ['OK'],
            cssClass: 'confirm-alert',
          });

          await alert.present();
          await alert.onWillDismiss();
          await this.modalCtrl.dismiss();

          if (avg(replies.map((r) => r.note)) >= SURVEY_NOTE_ASK_REVIEW) {
            await this.inAppReview.askReview();
          }
        }),
      );
  }

  setDishLike(restaurantId: number, dishId: number, isLikedByUser: boolean, isNotificationEnabled: boolean) {
    this.restaurantsRepo.setDishLike(restaurantId, dishId, isLikedByUser);
    return this.restaurantsApi.patchDishByDishidLikes({ DishId: dishId, body: { isLikedByUser, isNotificationEnabled } }).pipe(
      tap(() => {
        if (isLikedByUser) {
          this.interaction
            .showToaster({
              message: this.i18n.translate(isNotificationEnabled ? 'RESTAURANT_SERVICE.TOAST_DISH_LIKE_WITH_NOTIF' : 'RESTAURANT_SERVICE.TOAST_DISH_LIKE'),
              icon: 'heart-outline',
            })
            .then();
        }
      }),
    );
  }

  async openFluiditeModal(restaurantId: number) {
    // Trigger all fluidite requests
    this.restaurantsRepo.getRestaurantsWithFluidite().forEach((r) => {
      this.getFluiditeRates(r.id).subscribe();
    });

    const modal = await this.modalCtrl.create({
      component: RestaurantFluiditeComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        restaurantId,
      },
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView(`/restaurant/${restaurantId}/fluidite`, 'Restaurant | Fluidite');
  }

  async openSurveyCampaignModal(options?: { restaurantId?: number; openCurrentRestaurant?: boolean }) {
    // Trigger all survey campaign requests
    this.restaurantsRepo.getRestaurantsWithSurveyCampaign().forEach((r) => {
      this.getRestaurantSurvey(r.id, r.surveyCampaign!.surveyId, r.surveyCampaign!.campaignId).subscribe();
    });

    let restaurantId: number | null = null;
    if (options?.restaurantId) {
      restaurantId = options?.restaurantId;
    } else if (options?.openCurrentRestaurant) {
      restaurantId = this.restaurantsRepo.getActiveRestaurant()?.id || null;
    }

    const modal = await this.modalCtrl.create({
      component: SurveyCampaignModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        restaurantId: restaurantId,
      },
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView(`/restaurants/survey`, 'Restaurant | Survey');

    await modal.onDidDismiss();
  }

  async openDishDetailsModalFromCart(dishId: number) {
    const mapItems = (items: CartItem[]) => items.map((x) => ({ id: x.dishId, quantity: x.quantity }));

    const itemsBefore = mapItems(this.restaurantsRepo.getCart()!.items);

    const restaurant = this.restaurantsRepo.getActiveRestaurant()!;
    const { date } = this.restaurantsRepo.getCart()!;

    const dish = restaurant.menus
      .find((m) => isSameDay(ensureDate(m.date), ensureDate(date)))
      ?.dishFamilies.flatMap((f) => f.dishes)
      .find((d) => d.id === dishId);

    if (!dish) {
      // log error ?
      return;
    }

    await this.openDishDetailsModal(restaurant.id, dish, date, { fromCart: true, allowQuantityPicker: true });

    const itemsAfter = mapItems(this.restaurantsRepo.getCart()!.items);

    if (compareArray(itemsBefore, itemsAfter)) {
      this.checkCart().subscribe();
    }
  }

  async openFormulaDetailsModalFromCart(formula: CartFormula) {
    const mapFormulas = (formulas: CartFormula[]) => formulas.map((x) => ({ id: x.formulaId, quantity: x.quantity }));

    const formulasBefore = mapFormulas(this.restaurantsRepo.getCart()!.formulas);

    const restaurant = this.restaurantsRepo.getActiveRestaurant()!;
    const { date } = this.restaurantsRepo.getCart()!;
    const menu = restaurant.menus.find((menu) => isSameDay(ensureDate(menu.date), ensureDate(date)));
    if (menu) {
      const selectableMenu: SelectableMenu = await firstValueFrom(this.restaurantsRepo.selectableMenu$(menu));
      const selectableFormula = selectableMenu.formulas?.find((f) => f.id === formula.formulaId);
      if (!selectableFormula) {
        return;
      }

      await this.openFormulaModal(selectableFormula, restaurant.id, date, { cartFormula: formula });

      const formulasAfter = mapFormulas(this.restaurantsRepo.getCart()!.formulas);
      if (compareArray(formulasBefore, formulasAfter)) {
        this.checkCart().subscribe();
      }
    }
  }

  async openDishDetailsModal(restaurantId: number, dish: DishDto, menuDate: Date, options?: { fromCart: boolean; allowQuantityPicker: boolean }) {
    const modal = await this.modalCtrl.create({
      component: DishDetailsComponent,
      componentProps: { dish, restaurantId, menuDate, showQuantityPicker: options?.allowQuantityPicker },
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      cssClass: 'card-modal',
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView(`/restaurants/${restaurantId}/dishes/${dish.id}`, 'Dish | Details');

    const result = await modal.onDidDismiss();
    if (result.role === OPEN_CART_MODAL_ROLE && !options?.fromCart) {
      await this.openCartModal();
    }
  }

  async openTypologiesModal(title: string, subtitle: string, typologies: Array<TypologyDto>) {
    const modal = await this.modalCtrl.create({
      component: TypologiesModalComponent,
      componentProps: { title, subtitle, typologies },
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      cssClass: 'card-modal',
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/dish/typologies', 'Dish | Typologies');
  }

  async openNutriscoreModal() {
    const modal = await this.modalCtrl.create({
      component: NutriscoreModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      cssClass: 'card-modal',
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/dish/nutriscore', 'Dish | Nutriscore');
  }

  async openCarbonscoreModal() {
    const modal = await this.modalCtrl.create({
      component: CarbonscoreModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      cssClass: 'card-modal',
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/dish/carbonscore', 'Dish | Carbonscore');
  }

  async openGreenscoreModal() {
    const modal = await this.modalCtrl.create({
      component: GreenscoreModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      cssClass: 'card-modal',
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/dish/greenscore', 'Dish | Greenscore');
  }

  async openAllergensModal() {
    const modal = await this.modalCtrl.create({
      component: AllergensModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      cssClass: 'card-modal',
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/dish/allergens', 'Dish | Allergens');
  }

  async openSelectDishInformationModal() {
    const currentDishInformation = this.restaurantsRepo.getCurrentDishInformation();

    const infos: DishInformationEnum[] = this.restaurantsRepo.getAvailableDishInformations();

    const filterOptions = this.i18n.getOptions<DishInformationEnum>('DishInformationEnum', infos);

    const modal = await this.modalCtrl.create({
      component: SelectDishInformationComponent,
      componentProps: {
        currentDishInformation,
        filterOptions,
      },
      breakpoints: [0, 1],
      initialBreakpoint: 1,
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView(`/dishes/settings`, `Dishes | Settings`);
    const modalResult = await modal.onDidDismiss<DishInformationEnum>();
    if (modalResult?.data && modalResult.data !== currentDishInformation) {
      this.restaurantsRepo.setCurrentDishInformation(modalResult.data);
    }

    return true;
  }

  async openDescription(restaurant: Restaurant) {
    const modal = await this.modalCtrl.create({
      component: RestaurantDescriptionComponent,
      componentProps: {
        restaurant,
      },
      breakpoints: [0, 1],
      initialBreakpoint: 1,
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView(`/restaurants/${restaurant.id}/description`, 'Restaurant | Description');
  }

  addOrUpdateFormulaInCart(formula: CartFormula) {
    this.restaurantsRepo.addOrUpdateFormulaInCart(formula);
  }

  deleteFormulaFromCart(formula: CartFormula) {
    this.restaurantsRepo.deleteFormulaFromCart(formula);
  }

  addDishToCart(dish: DishDto) {
    this.restaurantsRepo.addDishToCart(dish);
  }

  removeDishFromCart(dishId: number) {
    this.restaurantsRepo.removeDishFromCart(dishId);
  }

  async openCartModal() {
    const modal = await this.modalCtrl.create({
      component: CartModalComponent,
      id: 'CartModalComponent',
      cssClass: 'cart-modal',
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView(`/restaurants/cart`, 'Restaurant | Cart');
  }

  async openFormulaModal(formula: SelectableFormula, restaurantId: number, date: Date, options?: { cartFormula: CartFormula }) {
    const modal = await this.modalCtrl.create({
      component: FormulaModalComponent,
      id: 'FormulaModalComponent',
      cssClass: 'cart-modal', // use same style as cart
      componentProps: {
        formula,
        restaurantId,
        date,
        cartFormula: options?.cartFormula,
      },
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView(`/restaurants/formula`, 'Restaurant | Formula');

    await modal.onDidDismiss();
  }

  /** If no menu on this date or orderlimitDate is passed, we reset the cart
   * return true if cart exists and is valid
   */
  async ensureCartIsValid() {
    const cart = this.restaurantsRepo.getCart()!;
    if (!cart) return false;

    if (this.restaurantsRepo.getActiveRestaurant()!.id !== cart.restaurantId) {
      this.restaurantsRepo.resetCart();

      return false;
    }

    if (!cart.allowCartRetrieval) {
      this.restaurantsRepo.resetCart();
      return false;
    }

    const menu = this.restaurantsRepo.getRestaurantMenuByDate(cart.restaurantId, cart.date);

    if (!menu || isAfter(new Date(), ensureDate(menu.orderLimitDate!))) {
      this.restaurantsRepo.resetCart();
      await this.interaction.showToaster({ message: this.i18n.translate('RESTAURANT_SERVICE.CART_EXPIRED'), color: 'warning' });
      return false;
    }

    return true;
  }

  async continuePreviousCart() {
    if (!(await this.ensureCartIsValid())) return;

    // ensureCartIsValid passed so we can assert cart and menu are valid
    const { date, restaurantId, service } = this.restaurantsRepo.getCart()!;

    const menu = this.restaurantsRepo.getRestaurantMenuByDate(restaurantId, date)!;

    await this.openMenu(menu, service);
  }

  checkCart() {
    const currentCart = this.restaurantsRepo.getCart()!;
    const request: CheckCartRequest = {
      date: ensureDate(currentCart.date).toISOString(),
      items: currentCart?.items,
      restaurantId: currentCart.restaurantId,
      formulas: currentCart?.formulas,
    };

    return this.ordersApi.postCheckcart({ body: request }).pipe(
      this.restaurantsRepo.checkedCartDS.trackRequestStatus(),
      tap((result) => {
        this.restaurantsRepo.setCheckedCart(result);
      }),
    );
  }

  createOrder(deliveryLocationId: number, comments?: string) {
    const currentCart = this.restaurantsRepo.getCart()!;
    const request: CreateOrderRequest = {
      date: ensureDate(currentCart.date).toISOString(),
      items: currentCart?.items,
      formulas: currentCart?.formulas,
      restaurantId: currentCart.restaurantId,
      deliveryLocationId,
      service: currentCart.service,
      comments: comments,
    };

    return this.ordersApi.postOrders({ body: request }).pipe(
      this.restaurantsRepo.trackRequestStatus(RestaurantsStatusKeys.CreateOrder),
      mergeMap(async (result) => {
        if (result) {
          const restaurant = this.restaurantsRepo.getRestaurantById(currentCart.restaurantId)!;

          let message = this.i18n.translate(`RESTAURANT_SERVICE.CREATE_ORDER_SUCCESS_ALERT.MESSAGE`, {
            date: formatWithLang(ensureDate(request.date), 'dd MMMM yyyy', this.accountRepo.getLanguage()!),
          });

          if (restaurant.sousType === RestaurantSousTypeValues.AntiGaspi) {
            this.getRestaurantMenu(restaurant.id, { initMenuFilter: false }).subscribe();
            message = this.i18n.translate(`RESTAURANT_SERVICE.CREATE_ORDER_SUCCESS_ALERT.MESSAGE_ANTIGASPI`, {
              date: formatWithLang(ensureDate(restaurant.closedAt!), 'HH:mm', this.accountRepo.getLanguage()!),
            });
          }

          const OpenOrderRole = 'OPEN_ORDER';
          const alert = await this.alert.create({
            header: this.i18n.translate('RESTAURANT_SERVICE.CREATE_ORDER_SUCCESS_ALERT.HEADER'),
            message,
            buttons: [
              {
                text: this.i18n.translate('RESTAURANT_SERVICE.CREATE_ORDER_SUCCESS_ALERT.SEE_ORDER'),
                cssClass: 'primary-color',
                role: OpenOrderRole,
              },
              'OK',
            ],
            cssClass: 'confirm-alert',
          });

          await alert.present();

          this.restaurantsRepo.setCreateOrderSuccess({ orderId: result.orderId, date: request.date, service: request.service });

          // two modals to close (cart modal + menu selection modal)
          if (restaurant.sousType === RestaurantSousTypeValues.Scorp) {
            await this.modalCtrl.dismiss(undefined, undefined, 'MenuSelectionModalComponent');
          }
          await this.modalCtrl.dismiss(undefined, undefined, 'CartModalComponent');

          const dismissResult = await alert.onDidDismiss();

          return { id: result.orderId, openModal: dismissResult.role === OpenOrderRole };
        }

        return null;
      }),
    );
  }

  async openMenu(menu: Menu, forceService?: RestaurantServiceValues) {
    const orderSettings = this.restaurantsRepo.getActiveRestaurantOrderSettings()!;
    const services: RestaurantServiceData[] = orderSettings.availableServices.map((s) => ({ service: s, existingOrderId: null }));
    const existingOrdersOfDay = this.restaurantsRepo.getRestaurantOrdersByDate(ensureDate(menu.date));
    existingOrdersOfDay.forEach((o) => {
      const service = services.find((s) => s.service === o.service);
      if (service) {
        service.existingOrderId = o.orderId;
      } else {
        services.push({ service: o.service, existingOrderId: o.orderId });
      }
    });

    let isOrderDailyLimitReached = false;

    if (!isNullOrUndefined(orderSettings.maxOrdersByDay)) {
      isOrderDailyLimitReached = existingOrdersOfDay.length >= orderSettings.maxOrdersByDay;
    }

    let serviceData: RestaurantServiceData | undefined = undefined;

    if (forceService) {
      serviceData = services.find((s) => s.service === forceService);
    } else if (services.length > 1) {
      const modal = await this.modalCtrl.create({
        component: ServiceSelectionModalComponent,
        componentProps: {
          services,
          isOrderDailyLimitReached,
        },
        cssClass: 'service-selection-modal',
        handle: false,
      });

      await modal.present();
      const modalResult = await modal.onDidDismiss<RestaurantServiceData | null>();

      // a service was selected
      serviceData = modalResult?.data as RestaurantServiceData;
    } else {
      serviceData = services[0];
    }

    if (serviceData) {
      if (serviceData.existingOrderId) {
        return { existingOrderId: serviceData.existingOrderId };
      } else {
        // init cart if new one only
        if (!forceService) {
          this.restaurantsRepo.initCart({
            restaurantId: this.restaurantsRepo.getActiveRestaurant()!.id,
            date: ensureDate(menu.date),
            service: serviceData.service,
            allowCartRetrieval: this.restaurantsRepo.isCartRetrivealEnabledOnActiveRestaurant(),
          });
        }

        await this.openMenuSelectionModal(menu);
      }
    }

    return {};
  }

  async tryPromptAntiGaspiAlert(source: AntiGaspiAlertSource) {
    const restaurant = this.restaurantsRepo.getActiveRestaurant();
    if (!restaurant) return;

    if (restaurant.sousType === RestaurantSousTypeValues.AntiGaspi) {
      if (this.accountRepo.getIsElligibleToAntiGaspiAlert(restaurant.id, source)) {
        // prompt anti gaspi alert
        this.accountRepo.addAntiGaspiAlertHistory({ date: new Date(), restaurantId: restaurant.id, source });

        const isAntiGaspiAlertResult = await this.i18n.checkSimpleAlert('RESTAURANT_SERVICE.ANTIGASPI_ALERT');

        if (isAntiGaspiAlertResult) {
          const typeNotif = Capacitor.isNativePlatform() ? TypeNotification.Push : TypeNotification.Email;

          this.notificationsService.getNotificationSettingByFlag(NotificationParameterFlags.AntiGaspi, typeNotif).subscribe(async (setting) => {
            const func_updateSettingIfNeeded = () =>
              (setting
                ? of(true)
                : this.notificationsService.updateNotificationSetting(NotificationParameterFlags.AntiGaspi, typeNotif, true).pipe(map((_) => true))
              ).pipe(
                tap({
                  next: async (_) => {
                    await this.interaction.showToaster({ message: this.i18n.translate('RESTAURANT_SERVICE.ANTIGASPI_ALERT_ACCEPTED'), duration: 5000 });
                  },
                  error: async (error: AppError) => {
                    await this.interaction.showToaster({ message: this.i18n.translateError(error.errorKey), color: 'danger', duration: 5000 });
                  },
                }),
              );

            if (typeNotif === TypeNotification.Push) {
              const hasAlreadyAcceptedPushNotifs = await this.pushNotifService.hasAcceptedPushNotifications();
              // check authorization
              if (!hasAlreadyAcceptedPushNotifs) {
                const pushNotifRegisterResult = await this.pushNotifService.register();
                if (!pushNotifRegisterResult) {
                  await this.interaction.showToaster({
                    message: this.i18n.translate('RESTAURANT_SERVICE.ANTIGASPI_ALERT_DENIED'),
                    color: 'danger',
                    duration: 5000,
                  });
                  return;
                }
              }

              func_updateSettingIfNeeded().subscribe();
            } else {
              // web
              func_updateSettingIfNeeded().subscribe();
            }
          });
        }
      }
    }
  }

  private async openMenuSelectionModal(menu: Menu) {
    const modal = await this.modalCtrl.create({
      component: MenuSelectionModalComponent,
      id: 'MenuSelectionModalComponent',
      componentProps: {
        menu,
        date: menu.date,
        restaurantId: this.restaurantsRepo.getActiveRestaurant()!.id,
        mode: 'calendar',
      },
      cssClass: 'menu-selection-modal',
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView(`/restaurants/menu-selection`, 'Restaurant | Menu Selection');
  }
}
